/**
 * The Concepts/Guides/Venture Graph/System Card nav data shared by `LearnHeader` (an organism) and
 * `LearnNavLink` (a molecule it renders).
 *
 * Lives here, not in either component, because it originally lived in
 * `LearnPageChrome.tsx` (the organism) and `LearnNavLink` imported its
 * `LearnNavSection` type from there — a molecule depending on an organism,
 * which also produced a source-level import cycle (organism → molecule →
 * organism) even though the `import type` erased it at runtime (Copilot
 * review, PR #860). A neutral `lib/learn` module, the same tier as
 * `urls.ts` and `domains.ts`, is what both component tiers are allowed to
 * depend on.
 *
 * Cross-compatible (no "use client", no server-only imports) so both
 * `LearnHeader` (server component) and `LearnNavLink` (client leaf) can
 * import it.
 */

import { FOUNDER_APPS } from "@/constants/founderApps";

/**
 * Which side of the Learn nav the current page belongs to. `"vocabulary"` is
 * the Concepts section's id and stays that way on purpose: it is the `section`
 * value sent with `landing_nav_clicked`, so renaming it would split the
 * analytics history. Only the label a visitor sees changed.
 */
export type LearnNavSection = "vocabulary" | "guides" | "venture-graph" | "system-card";

/**
 * The nav shared by every Learn and Venture Graph page: `LearnHeader`,
 * `VentureGraphHeader` and the `/learn` index all render it through
 * `LandingNav`. One definition so the headers cannot drift into different
 * labels or hrefs for the same sections.
 *
 * Concepts and Guides: the split reads by what each side is to a visitor
 * (single terms vs. multi-step processes), and the breadcrumb and footer link call
 * the first side the same thing.
 *
 * Venture Graph is the third section: a sibling library of sourced investor
 * data, not a Learn article type. Read the list through `learnNavItems`, not
 * directly — its routes 404 unless the deployment publishes them.
 *
 * System Card is the monthly AI model read under `/learn/system-card`. It
 * shares the Learn publication gate (`isLearnPublic()`) with Concepts and
 * Guides and is unfiltered like them; if Learn links on Venture Graph pages
 * ever need to follow that gate, all three move together.
 */
export type LearnNavItem = { section: LearnNavSection; label: string; href: string };

export const LEARN_NAV_ITEMS: LearnNavItem[] = [
  { section: "vocabulary", label: "Concepts", href: "/learn" },
  { section: "guides", label: "Guides", href: "/learn/guides" },
  { section: "venture-graph", label: "Venture Graph", href: "/venturegraph" },
  { section: "system-card", label: "System Card", href: "/learn/system-card" },
];

/**
 * The nav items a surface should render. `ventureGraphPublic` is
 * `isVentureGraphPublic()` read by a server caller, passed in rather than read
 * here so this module stays importable from client components.
 */
export function learnNavItems(ventureGraphPublic: boolean): LearnNavItem[] {
  return ventureGraphPublic
    ? LEARN_NAV_ITEMS
    : LEARN_NAV_ITEMS.filter((item) => item.section !== "venture-graph");
}

/**
 * Where every "go use Lev" CTA on a Lev Learn page points.
 *
 * The `?src=learn` is the whole reason this is a constant rather than `"/"`:
 * `ReferralCapture` turns it into the `lev_referral_id` cookie, and `/sign-up`
 * turns that into Clerk `unsafeMetadata`. `learn` is already a trusted referral
 * ID, so nothing else needs to change for this to resolve.
 *
 * It is not the only source of that attribution, and the gap it fills is
 * narrower than it looks. `referralIdFromPath` already derives `learn` from
 * `/learn/...`, so `ReferralCapture` writes the same cookie on any Learn page
 * view once hydrated. What the param adds is the window before that: these
 * pages are statically prerendered, so a reader can click a CTA before
 * hydration and leave with no referral at all. It also carries the source into
 * the destination pageview's `$current_url`, which is click-through data the
 * path-derived cookie never provided.
 *
 * Deliberately not applied to links *between* Learn pages: the param measures
 * clicking out of the library, and stamping it on internal navigation would
 * re-mint the referral on every breadcrumb click.
 */
export const LEARN_CTA_HREF = "/?src=learn";

/**
 * The CTA href for a guide page: `LEARN_CTA_HREF` plus which guide and where on
 * it (a stage id, or `"closing"` for the "Where Lev fits" box). `src=learn`
 * MUST stay first-class here, since it is what the signup referral resolves;
 * `guide` and `at` only add click-through detail to the destination pageview.
 */
export function learnGuideCtaHref(guideSlug: string, placement: string): string {
  const params = new URLSearchParams({ guide: guideSlug, at: placement });
  return `${LEARN_CTA_HREF}&${params.toString()}`;
}

/**
 * The "Start something" footer links on the Lev Learn family: every founder app
 * except Lev Learn, which has its own column (`LEARN_NAV_ITEMS`)
 * right beside it rather than appearing twice.
 */
export const LEARN_FOOTER_APPS = FOUNDER_APPS.filter((app) => app.href !== "/learn");

/** The company LinkedIn page, in the "Follow" footer column. */
export const LEV_LINKEDIN_URL = "https://www.linkedin.com/company/trylev/";
